import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';

import { useDataElTagGenerator } from '#utils/use-data-el-tag-generator.js';
import { AttributeIndicators } from './attribute-indicators.js';
import { ModelText } from '../model-text/model-text.js';
import { SignalTooltipContent } from './signal-tooltip-content.js';
import { ATTRIBUTE_INDICATORS } from './utils.js';

import type { HilSignal, HilSignalAttributes } from '@cadstrom/backend/shared';
import type { ChipProps } from '@mui/material/Chip';
import type { Ref } from 'react';

/**
 * Visual treatment for a compact signal chip:
 * - `filled` — solid attribute colour with white text.
 * - `soft` — a light, outlined attribute-coloured treatment.
 * - `dots` — a plain chip that indicates attributes with inline dots instead of colouring the chip.
 */
export type SignalChipAppearance = 'filled' | 'soft' | 'dots';

/**
 * Priority order for picking a signal's single "primary" attribute to colour a compact chip by.
 * Bound to `HilSignalAttributes` so a typo or a future attribute is caught at compile time.
 */
const PRIMARY_ATTRIBUTE_ORDER = [
	'power',
	'ground',
	'analog',
	'digital',
	'exposedPad',
	'activeLow',
	'noConnect',
] as const satisfies readonly (keyof HilSignalAttributes)[];

/**
 * Widest a chip may grow. Pin names can list every alternate function of a pin and run far wider than
 * any column, so the chip is bounded on its own rather than relying on each container to constrain it.
 */
const MAX_CHIP_WIDTH = 'min(100%, 20rem)';

export interface SignalChipProps {
	signal: HilSignal;
	showAttributes?: boolean;
	disableTooltip?: boolean;
	tooltipDisableInteractive?: boolean;
	/** When provided, the chip shows a delete (×) button wired to this handler. */
	onDelete?: ChipProps['onDelete'];
	/**
	 * Compact "net-chip" style — smaller, subtle border. The `filled`/`soft` appearances drop the
	 * inline attribute dots (the colour conveys the attribute); the `dots` appearance keeps them.
	 */
	compact?: boolean;
	/**
	 * For a compact chip: `filled` (solid colour, white text — the default), `soft` (a light, outlined
	 * tinted treatment), or `dots` (a plain chip with inline attribute dots). See
	 * {@link SignalChipAppearance}.
	 */
	appearance?: SignalChipAppearance;
	ref?: Ref<HTMLDivElement>;
	allSignals: HilSignal[];
	/** The chip's tab index, for a deletable chip inside a grid cell that keeps the grid to one tab stop. */
	tabIndex?: 0 | -1;
}

/**
 * A standardized chip component for displaying HIL signals with a formatted tooltip.
 * Shows the signal name on the chip and displays detailed signal information on hover.
 */
export function SignalChip({
	signal,
	showAttributes = true,
	disableTooltip = false,
	tooltipDisableInteractive = false,
	onDelete,
	compact = false,
	appearance = 'filled',
	ref,
	allSignals,
	tabIndex,
}: SignalChipProps) {
	const el = useDataElTagGenerator(SignalChip);

	// Get active attributes for indicators
	const activeAttributes = Object.entries(signal.attributes)
		.filter(([, value]) => value === true)
		.map(([key]) => key)
		.filter((key) => key in ATTRIBUTE_INDICATORS);

	// In compact mode, colour the whole chip by its primary attribute (the app's attribute palette) —
	// except for the `dots` appearance, which keeps a plain chip and conveys attributes via inline dots.
	const showAttributeDots = showAttributes && (!compact || appearance === 'dots');
	const primaryAttribute = PRIMARY_ATTRIBUTE_ORDER.find((attr) => activeAttributes.includes(attr));
	const accentColor =
		appearance !== 'dots' && primaryAttribute ? ATTRIBUTE_INDICATORS[primaryAttribute]?.color : undefined;
	const compactSx =
		!compact ? undefined
		: !accentColor ?
			// No primary attribute to colour by — a plain subtle outline.
			{ borderColor: 'var(--color-gray-a4)' }
		: appearance === 'soft' ?
			// Light "outline" style: a faint attribute-coloured fill with the colour as border and text.
			{
				backgroundColor: `color-mix(in srgb, ${accentColor} 12%, transparent)`,
				borderColor: `color-mix(in srgb, ${accentColor} 40%, transparent)`,
				color: accentColor,
				textShadow: 'none',
				'& .MuiChip-deleteIcon': {
					color: `color-mix(in srgb, ${accentColor} 55%, transparent)`,
					filter: 'none',
					'&:hover': { color: accentColor },
				},
			}
			// Filled "net-chip" style: solid attribute colour with white text, matching the app's filled
			// chips and the attribute pills shown in the tooltip.
		:	{
				backgroundColor: accentColor,
				borderColor: accentColor,
				color: '#fff',
				// The theme gives outlined chips a near-white text-shadow (for dark text on a light fill);
				// on this solid fill with white text it just halos the text, so drop it.
				textShadow: 'none',
				'& .MuiChip-deleteIcon': {
					color: 'rgba(255, 255, 255, 0.7)',
					// Same as the label: the theme's near-white drop-shadow halos the white icon — drop it.
					filter: 'none',
					'&:hover': { color: '#fff' },
				},
			};

	return (
		<Tooltip
			title={
				disableTooltip ? null : (
					<SignalTooltipContent signal={signal} showAttributes={showAttributes} allSignals={allSignals} />
				)
			}
			enterDelay={1000}
			enterNextDelay={200}
			disableInteractive={tooltipDisableInteractive}
		>
			<Chip
				{...el()}
				ref={ref}
				variant='outlined'
				size={compact ? 'small' : undefined}
				onDelete={onDelete}
				tabIndex={tabIndex}
				sx={{ maxWidth: MAX_CHIP_WIDTH, ...compactSx }}
				classes={{
					label: 'min-w-0',
				}}
				label={
					<div className='flex min-w-0 items-center gap-0.75'>
						{/* Uppercased in CSS, not in the value: signal names are conventionally upper case on a
						    chip, but the stored name is what tooltips, copy and comparisons must keep seeing. */}
						<ModelText value={signal.name} className='truncate -my-1 min-w-0 py-1 uppercase' />
						{showAttributeDots && activeAttributes.length > 0 && (
							<AttributeIndicators attributes={activeAttributes} indicators={ATTRIBUTE_INDICATORS} />
						)}
					</div>
				}
			/>
		</Tooltip>
	);
}
